import { DomainCode } from '@australiangreens/ag-internal-components';

import { getCatCampaignsByIds } from '@/apis/catcampaigns';
import { getUsersByIds } from '@/apis/users';
import { AutocompleteResult, isTemplatePlaceholder } from '@/types';
import { CallingStatusFilter } from '@/types/searchFilterTypes';
import { formatTemplateCapableDateForFilterValue } from '@/util';
import {
  CALLING_STATUS_OPTIONS_IDS,
  mapCampaignsToTooltips,
} from '@/constants/filterCriteria/callingStatusOptions';
import moment from 'moment';
import { CallingStatusWidgetValue } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/CallingStatusWidget';
import { checkDateFieldValueIsValid } from './dateFieldValidation';
import type {
  FilterMapper,
  MapQbRulePropertiesToSearchFilterOptions,
  ValidationContext,
} from './filterMapperTypes';
import { loadLists } from './listIdsLoader';

export const loadCampaigns = async ({
  token,
  campaignIds,
  domainCode,
}: {
  token: string;
  campaignIds: number[];
  domainCode: DomainCode;
}) => {
  if (campaignIds.length === 0) return { campaigns: [], isMissing: false };
  const campaigns = (await getCatCampaignsByIds(token, campaignIds, domainCode)).map(
    mapCampaignsToTooltips
  );
  return { campaigns, isMissing: campaigns.length !== campaignIds.length };
};

export const loadUsers = async ({ token, userSubs }: { token: string; userSubs: string[] }) => {
  if (userSubs.length === 0) return { users: [], isMissing: false };
  const users = (await getUsersByIds(token, userSubs)).map(({ id, name }) => ({ id, label: name }));
  return { users, isMissing: users.length !== userSubs.length };
};

export const CallingStatusMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const {
      callingstatuses,
      callingstatusDateRange: { displayedEndDate, displayedStartDate, callingPeriodQuantity },
    }: CallingStatusWidgetValue = value[0];

    const isNoCallingStatusSelected =
      !isTemplatePlaceholder(callingstatuses) && callingstatuses.length === 0;

    const callingPeriodQuantityIsNan =
      !isTemplatePlaceholder(callingPeriodQuantity) && isNaN(callingPeriodQuantity);

    const isDisplayedStartDateInValid =
      !isTemplatePlaceholder(displayedStartDate) && !checkDateFieldValueIsValid(displayedStartDate);

    const isDisplayedEndDateInValid =
      !isTemplatePlaceholder(displayedEndDate) && !checkDateFieldValueIsValid(displayedEndDate);

    const startDateAfterEndDate =
      !isTemplatePlaceholder(displayedEndDate) &&
      !isTemplatePlaceholder(displayedStartDate) &&
      displayedStartDate !== null &&
      displayedEndDate !== null &&
      moment(displayedStartDate).isAfter(moment(displayedEndDate));

    return (
      isNoCallingStatusSelected ||
      callingPeriodQuantityIsNan ||
      isDisplayedStartDateInValid ||
      isDisplayedEndDateInValid ||
      startDateAfterEndDate
    );
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  toFilter({ value }, options: MapQbRulePropertiesToSearchFilterOptions): CallingStatusFilter {
    if (!value) throw new Error('Missing value');
    // Why an array of length 1? Thats just the way it is the way we use it
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    const {
      callingstatuses,
      callingstatusDateRange,
      callingstatusLists,
      callingstatusUsers,
      callingstatusCampaigns,
    }: CallingStatusWidgetValue = value[0];

    const mappedCallingStatusLabels = isTemplatePlaceholder(callingstatuses)
      ? callingstatuses
      : callingstatuses.map(({ label }: AutocompleteResult) => label);

    // No special consideration needed for placeholder in this case
    const mappedCallingPeriodQuantity = callingstatusDateRange.callingPeriodQuantity;

    const { displayedEndDate, displayedStartDate } = callingstatusDateRange;

    const mappedDisplayedStartDate = formatTemplateCapableDateForFilterValue(
      displayedStartDate,
      isTemplatePlaceholder,
      null
    );

    const mappedDisplayedEndDate = formatTemplateCapableDateForFilterValue(
      displayedEndDate,
      isTemplatePlaceholder,
      null
    );

    const mappedCallingStatusListIds = isTemplatePlaceholder(callingstatusLists)
      ? callingstatusLists
      : callingstatusLists.map(({ id }) => id);

    const mappedCallingStatusUserSubs = isTemplatePlaceholder(callingstatusUsers)
      ? callingstatusUsers
      : callingstatusUsers.map(({ id }) => id as string);

    const mappedCallingStatusCampaignIds = isTemplatePlaceholder(callingstatusCampaigns)
      ? callingstatusCampaigns
      : callingstatusCampaigns.map(({ id }) => id);

    return {
      name: 'callingStatus',
      value: {
        callingStatusListIds: mappedCallingStatusListIds,
        callingStatusUserSubs: mappedCallingStatusUserSubs,
        callingStatusLabels: mappedCallingStatusLabels,
        callingStatusCampaignIds: mappedCallingStatusCampaignIds,
        callingStatusDateRange: {
          ...callingstatusDateRange,
          callingPeriodQuantity: mappedCallingPeriodQuantity,
          displayedStartDate: mappedDisplayedStartDate,
          displayedEndDate: mappedDisplayedEndDate,
        },
        // The quick search has a dedicated commspreferencesinclusion boolean,
        // but this is not user selectable in the advanced search (since user
        // can just wrap in a negation group) so it just always gets set to
        // true
        callingStatusInclusion: true,
      },
    };
  },

  async fromFilter(filter: CallingStatusFilter, { token, domainCode }: ValidationContext) {
    const {
      callingStatusCampaignIds,
      callingStatusDateRange,
      callingStatusInclusion,
      callingStatusLabels,
      callingStatusUserSubs,
      callingStatusListIds,
    } = filter.value;

    const mappedCallingstatuses = isTemplatePlaceholder(callingStatusLabels)
      ? callingStatusLabels
      : CALLING_STATUS_OPTIONS_IDS.filter((x) => callingStatusLabels.includes(x.label));

    let mappedCallingstatusLists, isListMissing;
    if (isTemplatePlaceholder(callingStatusListIds)) {
      mappedCallingstatusLists = callingStatusListIds;
      isListMissing = false;
    } else {
      ({ lists: mappedCallingstatusLists, isMissing: isListMissing } = await loadLists({
        token,
        listIds: callingStatusListIds,
      }));
    }

    let mappedCallingStatusCampaignIds, isCampaignMissing;
    if (isTemplatePlaceholder(callingStatusCampaignIds)) {
      mappedCallingStatusCampaignIds = callingStatusCampaignIds;
      isCampaignMissing = false;
    } else {
      ({ campaigns: mappedCallingStatusCampaignIds, isMissing: isCampaignMissing } =
        await loadCampaigns({
          token,
          domainCode,
          campaignIds: callingStatusCampaignIds,
        }));
    }

    let mappedUsers, isUserMissing;
    if (isTemplatePlaceholder(callingStatusUserSubs)) {
      mappedUsers = callingStatusUserSubs;
      isUserMissing = false;
    } else {
      ({ users: mappedUsers, isMissing: isUserMissing } = await loadUsers({
        token,
        userSubs: callingStatusUserSubs,
      }));
    }

    const { displayedStartDate, displayedEndDate } = callingStatusDateRange;

    const mappedDisplayedStartDate = isTemplatePlaceholder(displayedStartDate)
      ? displayedStartDate
      : displayedStartDate
        ? new Date(displayedStartDate)
        : null;

    const mappedDisplayedEndDate = isTemplatePlaceholder(displayedEndDate)
      ? displayedEndDate
      : displayedEndDate
        ? new Date(displayedEndDate)
        : null;

    const value: CallingStatusWidgetValue = {
      callingstatuses: mappedCallingstatuses,
      callingstatusDateRange: {
        ...callingStatusDateRange,
        displayedStartDate: mappedDisplayedStartDate,
        displayedEndDate: mappedDisplayedEndDate,
      },
      callingstatusinclusion: callingStatusInclusion,
      callingstatusUsers: mappedUsers,
      callingstatusLists: mappedCallingstatusLists,
      callingstatusCampaigns: mappedCallingStatusCampaignIds,
      errors: [
        ...(isUserMissing
          ? [{ field: 'users', message: 'Missing users', level: 'warning' as const }]
          : []),
        ...(isListMissing
          ? [{ field: 'lists', message: 'Missing lists', level: 'warning' as const }]
          : []),
        ...(isCampaignMissing
          ? [{ field: 'campaigns', message: 'Missing campaigns', level: 'warning' as const }]
          : []),
      ],
    };
    return {
      field: filter.name,
      operator: 'any in',
      value: [value],
    };
  },
};
